import { create } from 'zustand/react';
import { AssetReport, ReportOwnerType } from '../../domain/assetReport/type/AssetReport.type';
import { AssetGoals } from '../../domain/assetGoals/type/AssetGoals.type';
import { AssetPortfolio } from '../../domain/assetPortfolio/type/AssetPortfolio.type';
import { ApartmentCompare } from '../../domain/apartmentCompare/type/ApartmentCompare.type';
import { ManageAssetReportModalData } from '../../domain/assetReport/component/popup/ManageAssetReportModal';
import { ManageAssetGoalsModalData } from '../../domain/assetGoals/component/popup/ManageAssetGoalsModal';
import { ManageAssetPortfolioModalData } from '../../domain/assetPortfolio/component/popup/ManageAssetPortfolioModal';
import { ManageApartmentModalData } from '../../domain/apartmentCompare/component/popup/ManageApartmentModal';
import type { InvestmentIncome } from '../../domain/investmentIncome/type/InvestmentIncome.type';
import type { ManageInvestmentIncomeModalData } from '../../domain/investmentIncome/component/popup/ManageInvestmentIncomeModal';

interface AppStateLoadingCountStore {
  loadingCount: number;
  setLoadingCount: (loadingCount: number) => void;
  addCount: () => void;
  minusCount: () => void;
}

export const useAppStateLoadingCountStore = create<AppStateLoadingCountStore>((set) => ({
  loadingCount: 0,
  setLoadingCount: (loadingCount: number) => set({ loadingCount: loadingCount }),
  addCount: () => set((state) => ({ loadingCount: state.loadingCount + 1 })),
  minusCount: () => set((state) => ({ loadingCount: state.loadingCount - 1 })),
}));

interface AppStateAssetReportListStore {
  assetReportList: AssetReport[];
  setAssetReportList: (assetReportList: AssetReport[]) => void;
}

export const useAppStateAssetReportListStore = create<AppStateAssetReportListStore>((set) => ({
  assetReportList: [],
  setAssetReportList: (assetReportList: AssetReport[]) => set({ assetReportList: assetReportList }),
}));

interface AppStateCurrentAssetReportStore {
  currentAssetReport: AssetReport | undefined;
  setCurrentAssetReport: (currentAssetReport: AssetReport | undefined) => void;
}

export const useAppStateCurrentAssetReportStore = create<AppStateCurrentAssetReportStore>((set) => ({
  currentAssetReport: undefined,
  setCurrentAssetReport: (currentAssetReport) => set({ currentAssetReport: currentAssetReport }),
}));

interface AppStatePrevAssetReportStore {
  prevAssetReport: AssetReport | undefined;
  setPrevAssetReport: (prevAssetReport: AssetReport | undefined) => void;
}

export const useAppStatePrevAssetReportStore = create<AppStatePrevAssetReportStore>((set) => ({
  prevAssetReport: undefined,
  setPrevAssetReport: (prevAssetReport) => set({ prevAssetReport: prevAssetReport }),
}));

interface AppStateAssetGoalsListStore {
  assetGoalsList: AssetGoals[];
  setAssetGoalsList: (assetGoalsList: AssetGoals[]) => void;
}

export const useAppStateAssetGoalsListStore = create<AppStateAssetGoalsListStore>((set) => ({
  assetGoalsList: [],
  setAssetGoalsList: (assetGoalsList: AssetGoals[]) => set({ assetGoalsList: assetGoalsList }),
}));

interface AppStateAssetPortfolioListStore {
  assetPortfolioList: AssetPortfolio[];
  setAssetPortfolioList: (assetPortfolioList: AssetPortfolio[]) => void;
}

export const useAppStateAssetPortfolioListStore = create<AppStateAssetPortfolioListStore>((set) => ({
  assetPortfolioList: [],
  setAssetPortfolioList: (assetPortfolioList: AssetPortfolio[]) => set({ assetPortfolioList: assetPortfolioList }),
}));

interface AppStateInvestmentIncomeListStore {
  investmentIncomeList: InvestmentIncome[];
  setInvestmentIncomeList: (investmentIncomeList: InvestmentIncome[]) => void;
}

export const useAppStateInvestmentIncomeListStore = create<AppStateInvestmentIncomeListStore>((set) => ({
  investmentIncomeList: [],
  setInvestmentIncomeList: (investmentIncomeList) => set({ investmentIncomeList }),
}));

interface AppStateManageInvestmentIncomeModalDataStore {
  manageInvestmentIncomeModalData?: ManageInvestmentIncomeModalData;
  setManageInvestmentIncomeModalData: (
    manageInvestmentIncomeModalData?: ManageInvestmentIncomeModalData,
  ) => void;
}

export const useAppStateManageInvestmentIncomeModalDataStore =
  create<AppStateManageInvestmentIncomeModalDataStore>((set) => ({
    manageInvestmentIncomeModalData: undefined,
    setManageInvestmentIncomeModalData: (manageInvestmentIncomeModalData) =>
      set({ manageInvestmentIncomeModalData }),
  }));

interface AppStateManageAssetReportModalDataStore {
  manageAssetReportModalData?: ManageAssetReportModalData;
  setManageAssetReportModalData: (manageAssetReportModalData?: ManageAssetReportModalData) => void;
}

export const useAppStateManageAssetReportModalDataStore = create<AppStateManageAssetReportModalDataStore>((set) => ({
  manageAssetReportModalData: undefined,
  setManageAssetReportModalData: (manageAssetReportModalData?: ManageAssetReportModalData) =>
    set({ manageAssetReportModalData: manageAssetReportModalData }),
}));

interface AppStateManageAssetGoalsModalDataStore {
  manageAssetGoalsModalData?: ManageAssetGoalsModalData;
  setManageAssetGoalsModalData: (manageAssetGoalsModalData?: ManageAssetGoalsModalData) => void;
}

export const useAppStateManageAssetGoalsModalDataStore = create<AppStateManageAssetGoalsModalDataStore>((set) => ({
  manageAssetGoalsModalData: undefined,
  setManageAssetGoalsModalData: (manageAssetGoalsModalData?: ManageAssetGoalsModalData) =>
    set({ manageAssetGoalsModalData: manageAssetGoalsModalData }),
}));

interface AppStateManageAssetPortfolioModalDataStore {
  manageAssetPortfolioModalData?: ManageAssetPortfolioModalData;
  setManageAssetPortfolioModalData: (manageAssetPortfolioModalData?: ManageAssetPortfolioModalData) => void;
}

export const useAppStateManageAssetPortfolioModalDataStore = create<AppStateManageAssetPortfolioModalDataStore>(
  (set) => ({
    manageAssetPortfolioModalData: undefined,
    setManageAssetPortfolioModalData: (manageAssetPortfolioModalData?: ManageAssetPortfolioModalData) =>
      set({ manageAssetPortfolioModalData: manageAssetPortfolioModalData }),
  }),
);

interface AppStateApartmentCompareListStore {
  apartmentCompareList: ApartmentCompare[];
  setApartmentCompareList: (apartmentCompareList: ApartmentCompare[]) => void;
}

export const useAppStateApartmentCompareListStore = create<AppStateApartmentCompareListStore>((set) => ({
  apartmentCompareList: [],
  setApartmentCompareList: (apartmentCompareList: ApartmentCompare[]) => set({ apartmentCompareList }),
}));

interface AppStateManageApartmentModalDataStore {
  manageApartmentModalData?: ManageApartmentModalData;
  setManageApartmentModalData: (manageApartmentModalData?: ManageApartmentModalData) => void;
}

export const useAppStateManageApartmentModalDataStore = create<AppStateManageApartmentModalDataStore>((set) => ({
  manageApartmentModalData: undefined,
  setManageApartmentModalData: (manageApartmentModalData?: ManageApartmentModalData) =>
    set({ manageApartmentModalData }),
}));

interface AppStateOwnerFilterStore {
  ownerFilter: ReportOwnerType | undefined;
  setOwnerFilter: (ownerFilter: ReportOwnerType | undefined) => void;
}

export const useAppStateOwnerFilterStore = create<AppStateOwnerFilterStore>((set) => ({
  ownerFilter: undefined,
  setOwnerFilter: (ownerFilter) => set({ ownerFilter }),
}));

interface MonthCompareModalData {
  isOpen: boolean;
  onClose: () => void;
  currentReport: AssetReport;
  assetReportList: AssetReport[];
}

interface AppStateMonthCompareModalDataStore {
  monthCompareModalData?: MonthCompareModalData;
  setMonthCompareModalData: (monthCompareModalData?: MonthCompareModalData) => void;
}

export const useAppStateMonthCompareModalDataStore = create<AppStateMonthCompareModalDataStore>(
  (set) => ({
    monthCompareModalData: undefined,
    setMonthCompareModalData: (monthCompareModalData?: MonthCompareModalData) =>
      set({ monthCompareModalData: monthCompareModalData }),
  }),
);
